/*
  This code is the intellectual property of Bulb Inc. and other software publishers;
  it may not be altered, copied or disclosed without prior written approval.
  Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { Inject, Injectable } from '@angular/core';
import type UppyT from '@uppy/core';
import { PluginTarget } from '@uppy/core';
import { BulbCKEStateService } from 'src/ckeditor/editor/services/bulb-cke-state.service';

import { AppConfigService } from '../../common/services/app-config/app-config.service';
import { LANGUAGE_MAP, UPPY_DASHBOARD_OPTIONS, WebcamOptions } from './uppy-config';
import {
  UppyCustomDashboardPluginsService,
} from './uppy-custom-dashboard-plugins/uppy-custom-dashboard-plugins.service';
import { BulbConfigurableOptions, UppyAllowedFileTypes } from './types';

/**
 * Provides methods for using the Uppy Dashboard plugin and
 * core Dashboard Plugins we use in every Uppy Instance.
 */
@Injectable({
  providedIn: 'root',
})
export class UppyDashboardPluginsService {

  private uppyDashboard: Promise<{ default: PluginTarget }>;

  constructor(
    @Inject('BulbAuthService') private bulbAuthService,
    private bulbCkeStateService: BulbCKEStateService,
    private configService: AppConfigService,
    private uppyCustomPluginSvc: UppyCustomDashboardPluginsService,
  ) { }

  async useUppyDashboardModal(uppyInstance: UppyT): Promise<void> {
    this.uppyDashboard = import('@uppy/dashboard');
    const [UppyDashboard, UserLanguage] = await Promise.all([
      this.uppyDashboard,
      import(`@uppy/locales/lib/${LANGUAGE_MAP[this.bulbAuthService.currentUser.language.toLowerCase()]}`),
    ]);
    uppyInstance
      .use(UppyDashboard.default as any, {
        ...UPPY_DASHBOARD_OPTIONS,
        locale: UserLanguage.default,
      });
  }

  async useCoreDashboardPlugins(uppyInstance: UppyT): Promise<void> {
    const [UppyDashboard, GoogleDrive, OneDrive] = await Promise.all([
      this.uppyDashboard,
      import('@uppy/google-drive'),
      import('@uppy/onedrive'),
    ]);
    uppyInstance
      .use(GoogleDrive.default, {
        companionUrl: this.configService.uppy.companionURL,
        target: UppyDashboard.default,
      })
      .use(OneDrive.default, {
        companionUrl: this.configService.uppy.companionURL,
        target: UppyDashboard.default,
      });
  }

  /**
   * Lazy loads optional plugins depending on the parameters supplied.
   *
   * @param uppyInstance the uppy instance to attach the plugins too.
   * @param options the options to decide which plugins to load.
   */
  async useOptionalDashboardPlugins(uppyInstance: UppyT, options: BulbConfigurableOptions): Promise<void> {
    const isProUser = this.bulbAuthService.currentUser.features.StorageFeature.maxStorageBytes === -1;

    // Audio plugin
    const UppyDashboard = await this.uppyDashboard;
    if (options.allowedFileTypes?.includes(UppyAllowedFileTypes.ANY)
      || options.allowedFileTypes?.includes(UppyAllowedFileTypes.AUDIO)) {
      const Audio = await import('@uppy/audio');
      if (isProUser) {
        uppyInstance.use(Audio.default, {
          target: UppyDashboard.default,
          showAudioSourceDropdown: true,
          preferredAudioMimeType: 'audio/wav',
        });
      } else {
        this.uppyCustomPluginSvc.usePremiumPlugin(uppyInstance, UppyDashboard.default, 'content_block_type_AUDIO');
      }
    }

    // Screen capture plugin
    if (options.allowedFileTypes?.includes(UppyAllowedFileTypes.ANY)
        || options.allowedFileTypes?.includes(UppyAllowedFileTypes.VIDEO)) {
      if (isProUser) {
        const ScreenCapture = await import('@uppy/screen-capture');
        uppyInstance.use(ScreenCapture.default, {
          target: UppyDashboard.default,
          preferredVideoMimeType: 'video/x-matroska',
        });
      } else {
        this.uppyCustomPluginSvc.usePremiumPlugin(uppyInstance, UppyDashboard.default, 'uppy_screencast');
      }
    }

    // Webcam plugin
    let showWebcam = false;
    if (options.allowedFileTypes?.includes(UppyAllowedFileTypes.ANY)
      || (options.allowedFileTypes?.includes(UppyAllowedFileTypes.IMAGE)
        && options.allowedFileTypes?.includes(UppyAllowedFileTypes.VIDEO))) {
      WebcamOptions.modes = [
        'video-audio',
        'video-only',
        'audio-only',
        'picture',
      ];
      showWebcam = true;
    } else if (options.allowedFileTypes?.includes(UppyAllowedFileTypes.IMAGE)) {
      WebcamOptions.modes = [
        'picture',
      ];
      showWebcam = true;
    } else if (options.allowedFileTypes?.includes(UppyAllowedFileTypes.VIDEO)) {
      WebcamOptions.modes = [
        'video-audio',
        'video-only',
        'audio-only',
      ];
      showWebcam = true;
    }

    if (showWebcam) {
      if (isProUser) {
        const Webcam = await import('@uppy/webcam');
        WebcamOptions.target = UppyDashboard.default;
        uppyInstance.use(Webcam.default, {
          ...WebcamOptions,
        });
      } else {
        this.uppyCustomPluginSvc.usePremiumPlugin(uppyInstance, UppyDashboard.default, 'uppy_camera');
      }
    }
  }

  /**
   * Adds the custom BULB sources that are enabled for the current Uppy dashboard.
   *
   * @param uppyInstance Uppy instance receiving the custom plugins
   * @param folderId Asset Library destination folder
   * @param useAssetLibrary Whether to add the Asset Library source
   * @param useEmbedPlugin Whether to add the embed source
   * @param uploadState Editor state associated with the upload
   * @param contentBlockData Editor content-block context
   * @param useAdobeExpressPlugin Whether to add Adobe Express
   * @param useCanvaPlugin Whether to add Canva when it is enabled in runtime configuration
   */
  async useCustomDashboardPlugins(
    uppyInstance: UppyT,
    folderId?: string,
    useAssetLibrary: boolean = false,
    useEmbedPlugin: boolean = false,
    uploadState?: any,
    contentBlockData?: any,
    useAdobeExpressPlugin?: boolean,
    useCanvaPlugin?: boolean,
  ): Promise<void> {
    const Dashboard = await this.uppyDashboard;
    const assetType = this.bulbCkeStateService.assetType.value || 'IMAGE';
    if (this.bulbAuthService.currentUser.features.UnsplashRestrictionFeature.value !== 'DISABLED'
      && assetType === 'IMAGE') {
      this.uppyCustomPluginSvc.useUnsplashPlugin(
        uppyInstance,
        Dashboard.default,
        folderId,
        uploadState,
        contentBlockData,
      );
    }

    if (useAssetLibrary) {
      this.uppyCustomPluginSvc.useAssetLibraryPlugin(
        uppyInstance,
        Dashboard.default,
        uploadState,
        contentBlockData,
      );
    }

    if (useEmbedPlugin) {
      this.uppyCustomPluginSvc.useEmbedPlugin(uppyInstance, Dashboard.default);
    }

    if (useAdobeExpressPlugin) {
      this.uppyCustomPluginSvc.useAdobeExpressPlugin(uppyInstance, Dashboard.default);
    }

    if (useCanvaPlugin) {
      this.uppyCustomPluginSvc.useCanvaPlugin(
        uppyInstance,
        Dashboard.default,
        assetType,
        uploadState,
        contentBlockData?.detail?.orientation,
      );
    }
  }
}
